/**
 * Product description — the behaviour for the structured description
 * (docs/product-description-template.html).
 *
 * The description is the source of truth and arrives complete in the HTML:
 * the part details, the copy, the specifications and every fitment row. This
 * layer adds what a long table needs — a search box (a year is a term like
 * any other), sortable columns, scroll and paginated views (automatic), a
 * count and page controls — and moves the copy and the specifications beside
 * the price. Rows are never removed or fetched: a row outside the view gets
 * the `hidden` attribute, and every row prints.
 *
 * Two automatic switches to the paginated view, each at most once a visit and
 * never after a manual choice: a confirmed add of THIS product to the cart,
 * and scrolling at least halfway down the page then returning near the top,
 * with recent wheel, touch, key or scrollbar input (a restored scroll
 * position or a scripted jump does not count). A fresh load starts in
 * "Scroll all". Nothing is stored. From Lucas's write-up
 * (temp/product-description, 2026-10-08).
 */
(function () {
  'use strict';

  const INPUT_WINDOW_MS = 1800;
  const MIN_SCROLL_RANGE = 200;
  const TOP_LIMIT_PX = 150;
  const PAGE_SIZES = [10, 25, 50];
  const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

  function el(tag, attrs, text) {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(attrs || {})) {
      if (value === false || value == null) continue;
      if (key === 'className') node.className = value;
      else node.setAttribute(key, value === true ? '' : String(value));
    }
    if (text != null) node.textContent = text;
    return node;
  }

  /** How many parts "(1) 2878068, (2) 7052178" lists: the sum of the counts, or the entries. */
  // "POL_7547438, 12POL_2872346" → "7547438, 2872346": each number past its last underscore.
function bareNumbers(text) {
  return String(text).replace(/\S*_(?=\S)/g, '');
}

function partsIncluded(value) {
    const counts = [...String(value).matchAll(/\((\d+)\)/g)].map((m) => Number(m[1]));
    if (counts.length) return counts.reduce((a, b) => a + b, 0);
    return String(value).split(/[,;]/).filter((s) => s.trim()).length;
  }

  /** Shopify's handleize, so a row can be matched to a vehicle collection key. */
  function handleize(text) {
    return String(text).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  class Description {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.root = wrapper.querySelector('[data-fmt-description]');
      if (!this.root || this.root.dataset.fmtReady) return;
      this.root.dataset.fmtReady = 'true';
      wrapper.fmtDescription = this;
      this.productId = wrapper.dataset.productId || '';
      this.variantIds = (wrapper.dataset.variantIds || '').split(',').filter(Boolean);
      this.trustAny = wrapper.dataset.fmtTestInput === 'any';
      this.prefix = this.root.id || `fmt-${Math.random().toString(36).slice(2, 7)}`;

      this.placeAside();
      // A product without fitment (apparel, riding gear — data-no-fitment on
      // the wrapper): a table in its description is dropped, not enhanced.
      const section = this.root.querySelector('[data-fmt-fitment]');
      if (section && wrapper.hasAttribute('data-no-fitment')) section.remove();
      else if (section) this.enhanceFitment(section);
    }

    /** The facts join the list under the title; the copy and the specifications go beside the price. */
    placeAside() {
      const page = this.wrapper.closest('.product-layout') || document;
      const facts = page.querySelector('[data-product-facts]');
      const details = this.root.querySelector('.fmt-details');
      if (facts && details) {
        const have = new Set([...facts.querySelectorAll('dt')].map((dt) => dt.textContent.trim().toLowerCase()));
        // Category stays the last row (Lucas, 2026-10-12).
        const category = facts.querySelector('[data-fact="category"]');
        for (const item of details.querySelectorAll('div')) {
          const dt = item.querySelector('dt');
          const dd = item.querySelector('dd');
          if (!dt || !dd) continue;
          let label = dt.textContent.trim();
          const value = dd.textContent.trim();
          if (!value) continue;
          // The part number is the MPN already (the SKU's bare number): no second row.
          if (/^part number$/i.test(label)) continue;
          // "Parts included" only says something past one part: "(1) 2878068" is the part itself.
          if (/^parts included$/i.test(label) && partsIncluded(value) < 2) continue;
          if (/^replacement\s*\/\s*substituted part numbers$/i.test(label)) label = 'Substitutions';
          if (have.has(label.toLowerCase())) continue;
          // Hidden for now (Theme settings, request 131), and a value the
          // feed left as its own token ("{{$vidsubsaut}}") is never shown.
          if (/^substitutions$/i.test(label) && facts.hasAttribute('data-hide-substitutions')) continue;
          if (/\{\{|\}\}/.test(value)) continue;
          // Part numbers arrive with the feed's prefix ("POL_7547438"): the
          // bare number is what is shown, as the MPN is (#114).
          const shown = /^(substitutions|parts included)$/i.test(label) ? bareNumbers(value) : value;
          const wrap = el('div', { className: 'product-facts__item', 'data-fact': handleize(label) });
          wrap.append(el('dt', {}, label), el('dd', {}, shown));
          if (category) facts.insertBefore(wrap, category); else facts.append(wrap);
          have.add(label.toLowerCase());
        }
        details.remove();
      }
      const copy = this.root.querySelector('.fmt-copy');
      const copySlot = page.querySelector('[data-product-copy]');
      // An empty element from the feed (no paragraphs, no rows) leaves its
      // heading hidden (#110).
      if (copy && copySlot) {
        copySlot.replaceChildren(...copy.childNodes);
        copy.remove();
        const block = copySlot.closest('[data-product-copy-slot]');
        if (block) block.hidden = !(copySlot.textContent.trim() || copySlot.querySelector('img'));
      }
      const specs = this.root.querySelector('.fmt-specs');
      const list = page.querySelector('[data-product-specs]');
      // No slot (Theme settings → Hidden for now): the rows go nowhere.
      if (specs && !list) specs.remove();
      if (specs && list) {
        for (const row of specs.querySelectorAll('tbody tr')) {
          const cells = [...row.children];
          if (cells.length < 2) continue;
          const name = cells[0].textContent.trim();
          const value = cells[1].textContent.trim();
          // The feed sends one blank row when there are no specifications.
          if (!name && !value) continue;
          list.append(el('dt', {}, name), el('dd', {}, value));
        }
        specs.remove();
        const block = list.closest('[data-product-specs-slot]');
        if (block) block.hidden = list.children.length === 0;
      }
    }

    enhanceFitment(section) {
      const table = section.querySelector('table');
      const tbody = table && table.tBodies[0];
      if (!tbody || !table.tHead) return;
      this.section = section;
      this.table = table;
      this.tbody = tbody;
      // A row the feed left short (a year and a make with no model) says
      // nothing a customer can act on: hidden, and not a record.
      this.records = Array.from(tbody.rows, (row) => {
        const values = Array.from(row.cells, (cell) => cell.textContent.trim());
        if (values.length < 3 || values.some((v) => !v)) { row.hidden = true; row.setAttribute('data-fmt-broken', ''); return null; }
        if (!row.dataset.fmtKey) row.dataset.fmtKey = handleize(values.join(' '));
        return { row, values, search: values.join(' ').toLowerCase() };
      }).filter(Boolean);

      this.page = 1;
      this.column = 0;
      this.direction = -1;
      this.mode = 'scroll';
      this.pageSize = PAGE_SIZES[0];
      this.manualChoice = false;
      this.autoUsed = false;
      this.armed = false;
      this.pendingReason = '';
      this.lastInputAt = -Infinity;
      this.previousY = window.scrollY || 0;
      this.framePending = false;

      this.build();
      this.wire();
      this.render();
      section.classList.add('fmt-fitment--enhanced');
    }

    build() {
      const s = this.section;
      const p = this.prefix;
      if (!this.table.id) this.table.id = `${p}-table`;

      // Head: the title it came with, a line under it, and — on the right,
      // where the models count was (Lucas, #115/#118) — the parts-diagram
      // button, which Liquid renders in its own row above the description;
      // part-diagram.js shows and hides it wherever it sits.
      const title = s.querySelector('.fmt-fitment__title') || s.querySelector('h2, h3, h4');
      const head = el('div', { className: 'fmt-fitment__head' });
      const headText = el('div');
      if (title) { title.replaceWith(head); headText.append(title); } else s.prepend(head);
      headText.append(el('p', { className: 'fmt-fitment__sub' }, 'Search the list to confirm your machine.'));
      head.append(headText);
      const diagram = this.root.closest('product-fitment')?.querySelector('[data-part-diagram-open]');
      if (diagram) head.append(diagram);

      // Toolbar: one search box (#116). A year is a search term like any
      // other; the view switches itself (below) and has no control.
      this.search = el('input', { id: `${p}-search`, type: 'search', placeholder: 'Search year, make or model…', autocomplete: 'off', 'aria-controls': this.table.id, className: 'field__input' });
      const searchField = el('div', { className: 'fmt-field fmt-field--search' });
      searchField.append(el('label', { for: this.search.id, className: 'fmt-field__label' }, 'Search'), this.search);
      const toolbar = el('div', { className: 'fmt-toolbar' });
      toolbar.append(searchField);

      // The table in a bounded scroll region, headings sticky.
      let wrap = this.table.parentElement;
      if (!wrap.classList.contains('fmt-fitment__wrap')) {
        wrap = el('div', { className: 'fmt-fitment__wrap', role: 'region', 'aria-label': 'Vehicle compatibility table', tabindex: '0' });
        this.table.replaceWith(wrap);
        wrap.append(this.table);
      }
      this.wrap = wrap;
      wrap.before(toolbar);
      this.empty = el('p', { className: 'fmt-fitment__empty', hidden: true }, 'No matching vehicles. Try a different year or model.');
      wrap.append(this.empty);

      const foot = el('div', { className: 'fmt-fitment__foot' });
      this.status = el('p', { className: 'fmt-fitment__count', role: 'status', 'aria-live': 'polite' });
      this.controls = el('nav', { className: 'fmt-fitment__pages', 'aria-label': 'Compatibility table pages', hidden: true });
      foot.append(this.status, this.controls);
      wrap.after(foot);

      // Sortable headings.
      this.headers = [...this.table.tHead.rows[0].cells];
      this.headers.forEach((th, index) => {
        const button = el('button', { type: 'button', className: 'fmt-fitment__sort' });
        button.append(document.createTextNode(th.textContent.trim()));
        button.append(el('span', { className: 'fmt-fitment__sort-icon', 'aria-hidden': 'true' }));
        th.replaceChildren(button);
        button.addEventListener('click', () => {
          this.direction = this.column === index ? -this.direction : 1;
          this.column = index;
          this.page = 1;
          this.render();
        });
      });
    }

    wire() {
      this.search.addEventListener('input', () => { this.page = 1; this.render(); });

      // A deferred automatic switch applies once focus leaves the description.
      this.root.addEventListener('focusout', () => setTimeout(() => this.tryAutomaticSwitch(), 0));

      // The customer's machines are marked by product-fitment.js; they lead.
      document.addEventListener('garage:changed', () => setTimeout(() => this.render(), 0));

      // A confirmed add of THIS product — never another product, never a failed request.
      document.addEventListener('cart:updated', (event) => {
        const detail = event.detail || {};
        if (detail.source !== 'add') return;
        const added = Array.isArray(detail.added) ? detail.added : [];
        const mine = added.some((item) => this.variantIds.includes(String(item.id)) || String(item.product_id) === this.productId);
        if (mine) this.requestAutomaticSwitch('cart');
      });

      // Recent real input, so a restored scroll position or a scripted jump
      // cannot arm the trigger. Not bot detection — a heuristic, no more.
      const remember = (event) => {
        if (!event.isTrusted && !this.trustAny) return;
        if (event.type === 'keydown') {
          if (!['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '].includes(event.key)) return;
          if (event.target instanceof Element && event.target.closest('input, select, textarea, [contenteditable="true"]')) return;
        }
        if (event.type === 'pointerdown' && event.clientX < document.documentElement.clientWidth) return;
        this.lastInputAt = performance.now();
      };
      ['wheel', 'touchstart', 'touchmove', 'keydown', 'pointerdown'].forEach((type) => window.addEventListener(type, remember, { passive: true }));
      window.addEventListener('scroll', () => {
        if (this.framePending) return;
        this.framePending = true;
        requestAnimationFrame(() => this.checkPageScroll());
      }, { passive: true });
    }

    render() {
      const tokens = this.search.value.toLowerCase().trim().split(/\s+/).filter(Boolean);
      const matched = this.records.filter((r) => tokens.every((t) => r.search.includes(t)));
      // The chosen order alone — newest year first by default. Nothing is
      // pinned: the garage's machines used to float to the top, which with
      // several machines saved read as a broken sort (Lucas, 2026-10-15,
      // request 176). The current machine keeps its mark where it falls.
      matched.sort((a, b) =>
        this.direction * collator.compare(a.values[this.column] || '', b.values[this.column] || '')
        || collator.compare(a.values[1] || '', b.values[1] || '')
        || collator.compare(a.values[2] || '', b.values[2] || '')
        || Number(b.values[0]) - Number(a.values[0]));

      const paginated = this.mode === 'pages';
      const limit = paginated ? this.pageSize : Math.max(matched.length, 1);
      const pageCount = Math.max(1, Math.ceil(matched.length / limit));
      this.page = Math.min(Math.max(1, this.page), pageCount);
      const offset = paginated ? (this.page - 1) * limit : 0;
      const visible = new Set(matched.slice(offset, offset + limit).map((r) => r.row));
      const matchedRows = new Set(matched.map((r) => r.row));
      // Hidden, never removed: every fitment stays in the page and prints.
      for (const r of this.records) r.row.hidden = !visible.has(r.row);
      const frag = document.createDocumentFragment();
      matched.forEach((r) => frag.append(r.row));
      this.records.filter((r) => !matchedRows.has(r.row)).forEach((r) => frag.append(r.row));
      this.tbody.append(frag);

      const names = ['year', 'make', 'model'];
      this.headers.forEach((th, index) => {
        const active = index === this.column;
        th.setAttribute('aria-sort', active ? (this.direction === 1 ? 'ascending' : 'descending') : 'none');
        th.querySelector('.fmt-fitment__sort-icon').textContent = active ? (this.direction === 1 ? '↑' : '↓') : '↕';
        th.querySelector('button').setAttribute('aria-label', `Sort by ${names[index] || th.textContent.trim()} ${active && this.direction === 1 ? 'descending' : 'ascending'}`);
      });

      this.empty.hidden = matched.length !== 0;
      const total = this.records.length;
      this.status.textContent = matched.length
        ? (paginated ? `Showing ${offset + 1}–${Math.min(offset + limit, matched.length)}` : `Showing all ${matched.length}`)
          + ` of ${matched.length} vehicles` + (matched.length !== total ? ` (${total} in all)` : '')
        : '0 matching vehicles';

      this.controls.replaceChildren();
      this.controls.hidden = !paginated;
      if (paginated && matched.length) {
        this.pageButton('Previous', this.page === 1, () => { this.page--; this.render(); });
        const numbers = [...new Set([1, this.page - 1, this.page, this.page + 1, pageCount])]
          .filter((n) => n >= 1 && n <= pageCount).sort((a, b) => a - b);
        let last = 0;
        for (const n of numbers) {
          if (last && n - last > 1) this.controls.append(el('span', { 'aria-hidden': 'true' }, '…'));
          this.pageButton(String(n), false, () => { this.page = n; this.render(); }, n === this.page);
          last = n;
        }
        this.pageButton('Next', this.page === pageCount, () => { this.page++; this.render(); });
      }
      this.wrap.scrollTop = 0;
    }

    pageButton(label, disabled, handler, current) {
      const button = el('button', { type: 'button', className: 'fmt-fitment__page', 'aria-label': /^\d+$/.test(label) ? `Page ${label}` : label });
      button.textContent = label;
      button.disabled = disabled;
      if (current) button.setAttribute('aria-current', 'page');
      button.addEventListener('click', handler);
      this.controls.append(button);
    }

    // Quietly: no notice tells the customer the view changed (Lucas, #117).
    chooseMode(value, manual) {
      // A manual preference always wins over later automatic events.
      if (manual) { this.manualChoice = true; this.pendingReason = ''; }
      this.mode = value;
      this.page = 1;
      this.render();
    }

    tryAutomaticSwitch() {
      if (!this.pendingReason || this.manualChoice || this.autoUsed) return;
      // Never replace a focused row or control while someone is using it.
      if (this.root.contains(document.activeElement)) return;
      this.pendingReason = '';
      this.autoUsed = true;
      const y = window.scrollY || 0;
      this.chooseMode('pages', false);
      // Keep the page where it was, as far as the shorter page allows.
      try {
        window.scrollTo({ top: Math.min(y, Math.max(0, document.documentElement.scrollHeight - window.innerHeight)), behavior: 'instant' });
      } catch { /* jsdom */ }
    }

    requestAutomaticSwitch(reason) {
      if (this.manualChoice || this.autoUsed || this.mode === 'pages') return;
      this.pendingReason = reason;
      this.tryAutomaticSwitch();
    }

    checkPageScroll() {
      this.framePending = false;
      const y = window.scrollY || 0;
      const range = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const recentInput = performance.now() - this.lastInputAt <= INPUT_WINDOW_MS;
      if (!this.manualChoice && !this.autoUsed && recentInput && range >= MIN_SCROLL_RANGE) {
        // Halfway means half of the OUTER page's scrolling distance.
        if (y > this.previousY && y >= range * 0.5) this.armed = true;
        const topBoundary = Math.min(TOP_LIMIT_PX, range * 0.1);
        if (this.armed && y < this.previousY && y <= topBoundary) this.requestAutomaticSwitch('scroll');
      }
      this.previousY = y;
    }
  }

  function boot() {
    document.querySelectorAll('[data-product-description]').forEach((wrapper) => new Description(wrapper));
  }

  window.ProductDescription = { boot, handleize };
  if (document.readyState === 'complete') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
